import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Spin, Modal, DatePicker, TimePicker, Input, Button, Card, Divider } from "antd";
import dayjs from "dayjs";
import * as recruitmentApi from "../../../../core/data/redux/services/recruitmentApi";
import { notify } from "../../../../services/toast.config";

const PublicSelfScheduling: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [interview, setInterview] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState("");

  useEffect(() => {
    if (token) loadDetails(token);
  }, [token]);

  const loadDetails = async (t: string) => {
    setLoading(true);
    try {
      const data = await recruitmentApi.getPublicSchedulingDetails(t);
      setInterview(data);
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.message || "Invalid or expired scheduling link");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      await recruitmentApi.confirmPublicScheduling(token, { status: "confirmed" });
      notify.success("Interview confirmed! We look forward to meeting you.");
      setSuccess(true);
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.message || "Failed to confirm interview");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReschedule = async () => {
    if (!token || !rescheduleReason) {
      notify.warn("Please provide a reason for rescheduling");
      return;
    }
    setSubmitting(true);
    try {
      await recruitmentApi.confirmPublicScheduling(token, { status: "reschedule", reason: rescheduleReason });
      notify.success("Reschedule request sent. Our team will contact you.");
      setSuccess(true);
      setIsRescheduleModalOpen(false);
    } catch (err: any) {
      notify.error(err?.data?.error?.message || err?.message || "Failed to send request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <Spin size="large" tip="Loading interview details..." />
      </div>
    );
  }

  if (success) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <Card className="text-center p-4 shadow-sm border-0 rounded-4" style={{ maxWidth: 500 }}>
          <div className="bg-success bg-opacity-10 text-success p-3 rounded-circle d-inline-flex mb-4">
            <i className="ti ti-check fs-1" />
          </div>
          <h2 className="fw-bold mb-3">Response Received</h2>
          <p className="text-muted mb-4">Thank you for your response. We have updated your interview status. You may close this window.</p>
          <Divider />
          <Link to="/careers" className="btn btn-primary rounded-pill px-4">Visit Careers Page</Link>
        </Card>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <Card className="text-center p-4 shadow-sm border-0 rounded-4" style={{ maxWidth: 500 }}>
          <i className="ti ti-alert-circle text-danger mb-3" style={{ fontSize: 50 }} />
          <h3>Invalid Link</h3>
          <p className="text-muted">This scheduling link is invalid or has expired.</p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh" }}>
      <div className="text-white py-5 position-relative overflow-hidden" style={{ background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)" }}>
        <div className="container text-center">
          <h1 className="fw-bold mb-2">Schedule Confirmation</h1>
          <p className="text-white-50">Please confirm your availability for the upcoming interview stage.</p>
        </div>
      </div>

      <div className="container py-5" style={{ marginTop: "-40px" }}>
        <div className="row justify-content-center">
          <div className="col-lg-7">
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="p-4 bg-light border-bottom">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold">{interview.round_name}</h5>
                  <span className="badge bg-primary rounded-pill px-3">{interview.round_type?.toUpperCase()}</span>
                </div>
              </div>
              
              <div className="card-body p-4 p-md-5">
                <div className="row g-4 mb-5">
                  <div className="col-sm-6">
                    <div className="d-flex align-items-center p-3 bg-light rounded-3">
                      <i className="ti ti-calendar-event fs-3 text-primary me-3" />
                      <div>
                        <div className="text-muted small text-uppercase fw-bold mb-1">Proposed Date</div>
                        <div className="fw-bold text-dark">
                          {dayjs(interview.scheduled_at).format("dddd, MMM D, YYYY")}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="d-flex align-items-center p-3 bg-light rounded-3">
                      <i className="ti ti-clock fs-3 text-primary me-3" />
                      <div>
                        <div className="text-muted small text-uppercase fw-bold mb-1">Time & Duration</div>
                        <div className="fw-bold text-dark">
                          {dayjs(interview.scheduled_at).format("h:mm A")} ({interview.duration_minutes} mins)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {interview.meeting_link && (
                  <div className="mb-5 p-3 rounded-3 border border-primary border-opacity-25 bg-primary bg-opacity-10">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <div className="fw-bold text-primary mb-1"><i className="ti ti-brand-zoom me-2" /> Meeting Location</div>
                        <div className="text-muted small">The interview will be conducted online.</div>
                      </div>
                      <a href={interview.meeting_link} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm rounded-pill px-4">
                        Join Link
                      </a>
                    </div>
                  </div>
                )}

                <div className="d-flex flex-column flex-md-row gap-3 mt-4">
                  <Button 
                    type="primary" 
                    size="large" 
                    block 
                    className="rounded-pill py-2" 
                    onClick={handleConfirm}
                    loading={submitting}
                    icon={<i className="ti ti-check me-1" />}
                  >
                    Confirm My Availability
                  </Button>
                  <Button 
                    size="large" 
                    block 
                    className="rounded-pill py-2"
                    onClick={() => setIsRescheduleModalOpen(true)}
                    disabled={submitting}
                    icon={<i className="ti ti-calendar-time me-1" />}
                  >
                    Request Reschedule
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Modal
        title="Request Reschedule"
        open={isRescheduleModalOpen}
        onCancel={() => setIsRescheduleModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsRescheduleModalOpen(false)}>Cancel</Button>,
          <Button key="submit" type="primary" loading={submitting} onClick={handleReschedule}>Send Request</Button>
        ]}
      >
        <p className="text-muted small mb-3">If you are unable to make the proposed time, please share your reasoning and preferred availability. Our team will get back to you.</p>
        <Input.TextArea 
          rows={4} 
          placeholder="e.g. I have a prior commitment at this time. I am available later in the day or tomorrow morning..." 
          value={rescheduleReason}
          onChange={(e) => setRescheduleReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default PublicSelfScheduling;
